import styled from "@emotion/styled";
import isValidDomain from "is-valid-domain";
import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

import {
  Button,
  DesignToken,
  FormField,
  Input,
  Loader,
  ModalPortal,
  ShadowToken,
  SpacingToken,
  Tab,
  TabsList,
  TabsWrapper,
  Text,
} from "@livechat/design-system-react-components";

import LinkStyledButton from "components/button/LinkStyledButton";
import { ExternalLink } from "components/external-link";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import ConfirmActionModal from "components/modals/ConfirmActionModal";
import { HelpCenter } from "constants/help-center";
import { SettingsSection } from "constants/settings/sections";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { isEmail } from "helpers/validate";
import useSettingsSection from "hooks/settings/use-settings-section";
import useModal from "hooks/use-modal";
import { SpamActions } from "store/entities/spam/actions";
import { ISpamEntry } from "store/entities/spam/interfaces";
import {
  selectBlockedEntries,
  selectBlockedEntriesLoading,
  selectTrustedEntries,
  selectTrustedEntriesLoading,
} from "store/entities/spam/selectors";
import { StackingContextLevel } from "styles/stacking-context-level";
import { withVar } from "themes/helpers";

import SettingsViewContainer from "../ViewContainer";
import { ContentWrapper, PageWrapper, SettingsHeader } from "../styled";

enum SpamListType {
  Trusted = "trusted",
  Blocked = "blocked",
}

interface IDeleteModalProps {
  entry: ISpamEntry;
  onDelete(): void;
  onClose(): void;
}

const DeleteModal: React.FC<IDeleteModalProps> = ({ entry, onDelete, onClose }) => (
  <ModalPortal zIndex={StackingContextLevel.Modal}>
    <ConfirmActionModal
      onClose={onClose}
      heading={entry.value.includes("@") ? "Delete address?" : "Delete domain?"}
      primaryAction={{
        label: "Delete",
        onClick: onDelete,
        actionType: "destructive",
      }}
      secondaryAction={{
        label: "Cancel",
        onClick: onClose,
      }}
    >
      <Text>You are about to delete the following {entry.value.includes("@") ? "address" : "domain"}:</Text>
      <Text bold>{entry.value}</Text>
    </ConfirmActionModal>
  </ModalPortal>
);

interface ISpamEntryListProps {
  items: ISpamEntry[];
  onDelete(id: string): void;
}

const SpamEntryList: React.FC<ISpamEntryListProps> = ({ items, onDelete }) => (
  <ListGrid>
    <ListHeader>
      <Text bold>Address name</Text>
    </ListHeader>
    <ListHeader>
      <Text bold>Action</Text>
    </ListHeader>
    {items.map(entry => (
      <HoverWrapper key={entry.id}>
        <Column>
          <Text>{isEmail(entry.value) ? entry.value : `@${entry.value}`}</Text>
        </Column>
        <Column>
          <LinkStyledButton
            data-testid="delete-button"
            color="errorColor"
            onClick={() => {
              onDelete(entry.id);
            }}
          >
            Delete
          </LinkStyledButton>
        </Column>
      </HoverWrapper>
    ))}
  </ListGrid>
);

interface IAddEntryControlProps {
  onAdd(entry: string): void;
  buttonLabel: string;
}

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const validateDomain = (name: string) => {
  if (!isValidDomain(name) || name.split(".").length === 1 || name.split(".").some(part => part.length > 63)) {
    return "Domain is invalid.";
  }

  return null;
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const validateEmail = (entry: string) => (isEmail(entry) ? null : "Email is invalid.");

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const validateEntry = (entry: string) => {
  if (entry.trim().length === 0) {
    return "Entry cannot be empty.";
  }

  if (entry.includes("@")) {
    return validateEmail(entry);
  }

  return validateDomain(entry);
};

const AddEntryControl: React.FC<IAddEntryControlProps> = ({ onAdd, buttonLabel }) => {
  const [entryDirty, setEntryDirty] = useState(false);
  const [entry, setEntry] = useState("");
  const [entryValidation, setEntryValidation] = useState(validateEntry(entry));

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEntryDirty(false);
    setEntry(event.target.value);
    setEntryValidation(validateEntry(event.target.value));
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleBlur = () => {
    setEntryDirty(true);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setEntryDirty(false);
    setEntry("");
    setEntryValidation(null);
    onAdd(entry);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if ((event.key === "Enter" || event.keyCode === 13) && entryValidation !== null) {
      setEntryDirty(true);
    }
  };

  return (
    <form onSubmit={handleSubmit} css={{ marginTop: withVar(SpacingToken.Spacing2) }}>
      <Flex gap={withVar(SpacingToken.Spacing2)} maxWidth="400px" flexWrap>
        <FormField error={entryDirty && entryValidation ? entryValidation : undefined}>
          <AddressInput
            id="entry"
            name="entry"
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyUp={handleKeyUp}
            error={entryDirty && entryValidation !== null}
            value={entry}
            placeholder="Type domain or email address…"
            inputSize="compact"
            data-1p-ignore
          />
        </FormField>
        <Button kind="primary" size="compact" type="submit" disabled={entryValidation !== null}>
          {buttonLabel}
        </Button>
      </Flex>
    </form>
  );
};

const TrustedList: React.FC = () => {
  const dispatch = useDispatch();

  const items = useSelector(selectTrustedEntries);
  const isLoading = useSelector(selectTrustedEntriesLoading);

  const [deleteModalID, deleteModal] = useModal<string | null>(null);

  const deleteEntry = items.find(i => i.id === deleteModalID);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onDelete = (id: string) => {
    deleteModal.show(id);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onDeleteEntry = () => {
    deleteModal.close();
    trackEngagement("Spam: Address removed from trusted list", EventPlace.Settings);

    if (deleteEntry) {
      dispatch(SpamActions.removeTrustedEntry({ id: deleteEntry.id }));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onAdd = (entry: string) => {
    trackEngagement("Spam: Address added to trusted list", EventPlace.Settings);
    dispatch(SpamActions.addTrustedEntry({ entry }));
  };

  useEffect(() => {
    if (isLoading) {
      dispatch(SpamActions.fetchTrustedList());
    }
  }, [dispatch, isLoading]);

  return (
    <>
      <Text>Email addresses and domains added to your trusted list won&lsquo;t be treated as spam.</Text>
      <AddEntryControl onAdd={onAdd} buttonLabel="Add to trusted" />
      <Block paddingV={withVar(SpacingToken.Spacing4)}>
        {isLoading ? (
          <Loader label="Loading…" />
        ) : (
          <>
            {items.length > 0 && <SpamEntryList items={items} onDelete={onDelete} />}
            {items.length === 0 && <p>There are no addresses in trusted list yet.</p>}
          </>
        )}
        {deleteEntry && <DeleteModal entry={deleteEntry} onDelete={onDeleteEntry} onClose={deleteModal.close} />}
      </Block>
    </>
  );
};

const BlockedList: React.FC = () => {
  const dispatch = useDispatch();

  const items = useSelector(selectBlockedEntries);
  const isLoading = useSelector(selectBlockedEntriesLoading);

  const [deleteModalID, deleteModal] = useModal<string | null>(null);

  const deleteEntry = items.find(i => i.id === deleteModalID);

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onDelete = (id: string) => {
    deleteModal.show(id);
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onDeleteEntry = () => {
    deleteModal.close();
    trackEngagement("Spam: Address removed from blocked list", EventPlace.Settings);
    if (deleteEntry) {
      dispatch(SpamActions.removeBlockedEntry({ id: deleteEntry.id }));
    }
  };

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const onAdd = (entry: string) => {
    dispatch(SpamActions.addBlockedEntry({ entry }));
    trackEngagement("Spam: Address added to blocked list", EventPlace.Settings);
  };

  useEffect(() => {
    if (isLoading) {
      dispatch(SpamActions.fetchBlockedList());
    }
  }, [dispatch, isLoading]);

  return (
    <>
      <Text>Email addresses and domains added to your blocked list will be treated as spam.</Text>
      <AddEntryControl onAdd={onAdd} buttonLabel="Add to blocked" />
      <Block paddingV={withVar(SpacingToken.Spacing4)}>
        {isLoading ? (
          <Loader label="Loading…" />
        ) : (
          <>
            {items.length > 0 && <SpamEntryList items={items} onDelete={onDelete} />}
            {items.length === 0 && <p>There are no addresses in blocked list yet.</p>}
          </>
        )}
        {deleteEntry && <DeleteModal entry={deleteEntry} onDelete={onDeleteEntry} onClose={deleteModal.close} />}
      </Block>
    </>
  );
};

const AddressesSection: React.FC = () => {
  const location = useLocation();
  const history = useHistory();

  useSettingsSection(SettingsSection.Spam);

  const currentList: SpamListType = location.hash === "#blocked" ? SpamListType.Blocked : SpamListType.Trusted;

  // FIXME: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const switchSection = (section: SpamListType) => {
    if (location.hash !== section) {
      history.push(`#${section}`);
    }
  };

  return (
    <>
      <SettingsHeader>Addresses</SettingsHeader>
      <Text>
        Add or remove addresses from the trusted and blocked lists. You can add an entire email address as well as an
        email domain.
      </Text>
      <Block marginV={withVar(SpacingToken.Spacing3)} paddingTop={withVar(SpacingToken.Spacing3)}>
        <TabsWrapper>
          <TabsList>
            <Tab onClick={() => switchSection(SpamListType.Trusted)} isSelected={currentList === SpamListType.Trusted}>
              Trusted
            </Tab>
            <Tab onClick={() => switchSection(SpamListType.Blocked)} isSelected={currentList === SpamListType.Blocked}>
              Blocked
            </Tab>
          </TabsList>
        </TabsWrapper>
      </Block>
      {currentList === SpamListType.Trusted && <TrustedList />}
      {currentList === SpamListType.Blocked && <BlockedList />}
    </>
  );
};

const Spam: React.FC = () => (
  <SettingsViewContainer title="Spam">
    <PageWrapper>
      <ContentWrapper>
        <Text>
          Prevent spam situations. Manage email addresses or email domains using trusted and blocked lists.{" "}
          <ExternalLink
            target="_blank"
            href={HelpCenter.SPAM_LIST}
            onClick={() => trackEngagement("Spam: Learn more clicked", EventPlace.Settings)}
          >
            Learn more
          </ExternalLink>
        </Text>
        <AddressesSection />
      </ContentWrapper>
    </PageWrapper>
  </SettingsViewContainer>
);

const AddressInput = styled(Input)`
  max-width: 100%;
  width: 250px;
`;

const ListGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 100px;
  grid-template-rows: 1fr;
`;

const ListHeader = styled.div`
  padding: var(${SpacingToken.Spacing2}) var(${SpacingToken.Spacing5});
  box-shadow: var(${ShadowToken.DividerBottom});
`;

const Column = styled.div`
  padding: var(${SpacingToken.Spacing3}) var(${SpacingToken.Spacing5});
  box-shadow: var(${ShadowToken.DividerBottom});
`;

const HoverWrapper = styled.div`
  display: contents;
  &:hover > div {
    background: ${withVar(DesignToken.SurfacePrimaryHover)};
  }
`;

export default Spam;
